import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useReactionHandler } from '../../../hooks/useReactionHandler';
import { ASSETS_BASE_URL } from '../../../lib/constants';
import { useAuthInfo } from '../../../lib/jwt';
import { CommentDtoType } from '../../../types/dto/CommentDtoType';
import { EntityType } from '../../../types/dto/EntityType';
import CommentInput from '../../custom/inputs/CommentInput';
import { Likes } from '../basic/Likes';
import Replies from '../basic/Replies';
import { UserContentContainer } from '../profile/UserContentContainer';
import { UserContentContainerHeader } from '../profile/UserContentContainerHeader';
import CommentReplies from './CommentReplies';

type CommentListItemProps = {
  itemClassName?: string;
  type: EntityType;
  comment: CommentDtoType;
  replyId?: number;
  linkedId: number;
  isCommentReplies?: boolean;
  onReply?: (commentId: number) => void;
  onAdded: (newItem: CommentDtoType) => any;
  onChanged: (changedItem: CommentDtoType) => any;
  onDeleted: (deletedItem: CommentDtoType) => any;
};

export function CommentListItem({
  itemClassName,
  type,
  comment,
  replyId,
  linkedId,
  isCommentReplies,
  onReply,
  onAdded,
  onChanged,
  onDeleted
}: CommentListItemProps) {
  const [showReplies, setShowReplies] = useState(false);
  const [isReply, setIsReply] = useState(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [replyUpdate, setReplyUpdate] = useState<boolean>(false);
  const [authInfo, token] = useAuthInfo(false);

  const likeHandler = useReactionHandler<CommentDtoType>({
    entity: comment,
    type: type,
    isComment: true,
    linkedEntityId: linkedId,
    onChanged: onChanged
  });

  const onShowAllClick = useCallback((value: boolean) => {
    setShowReplies(value);
    setReplyUpdate(true);
  }, []);

  const onReplyClick = (id: number) => {
    onReply(id);
    setIsReply(prevState => !prevState);
  };

  const deleteHandler = useCallback(async () => {
    let success = false;

    try {
      setIsDeleting(true);

      const response = await fetch(`/api/comments/${type}/${comment.id}`, {
        method: 'DELETE',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      success = response.status === 200;
    } catch (error) {
      toast.success('Something went wrong, please try again later', {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined
      });
    } finally {
      setIsDeleting(false);

      if (success) {
        onDeleted(comment);
      }
    }
  }, []);

  useEffect(() => {
    if (replyId !== comment.id) setIsReply(false);
  }, [replyId]);

  if (isDeleting) {
    return null;
  }

  return (
    <>
      <UserContentContainer
        spoilerShow={true}
        className={`${itemClassName} border-b-1 border-gray border-opacity-20`}
        author={comment.author}
        text={comment.text}
        footerClassName="flex gap-x-4"
        header={
          <UserContentContainerHeader
            author={comment.author}
            createdAt={comment.createdAt}
            deleteText="Delete comment"
            isAuth={!!authInfo?.id}
            onDelete={deleteHandler}
            selfContent={comment.userId === authInfo?.id}
          />
        }
        footer={
          <>
            <Likes
              userReactions={comment.userReactions}
              likesCount={comment.reactionsCount}
              size="small"
              onReacted={likeHandler}
            />
            {!isCommentReplies && (
              <Replies
                commentId={comment.id}
                repliesCount={comment.repliesCount ? comment.repliesCount : 0}
                size="small"
                onReply={onReplyClick}
                isReply={isReply}
              />
            )}
            {!isCommentReplies && (
              <div
                className={`cursor-pointer ${!showReplies ? '' : 'underline'}`}
                onClick={() => setShowReplies(prevState => !prevState)}
              >
                {comment.repliesCount && (
                  <p>&#8226; {!showReplies ? 'Show All' : 'Hide'}</p>
                )}
              </div>
            )}
          </>
        }
      >
        {comment.image && (
          <img
            className="object-contain px-4 pt-4 mx-auto"
            src={`${ASSETS_BASE_URL}${comment?.image}`}
          />
        )}
        {comment.animatedImage && (
          <img
            className="object-contain px-4 pt-4 mx-auto"
            src={`${comment?.animatedImage}`}
          />
        )}
      </UserContentContainer>
      {isReply && (
        <CommentInput
          className={`mb-4 mt-4 h-16 pl-10 pr-4
            border-b-1 border-gray border-opacity-20`}
          linkedId={linkedId}
          parentId={replyId}
          type={type}
          onSubmitted={onAdded}
          onShowReplies={onShowAllClick}
        />
      )}
      {showReplies && (
        <CommentReplies
          className={`${itemClassName}`}
          userId={authInfo?.id}
          linkedId={linkedId}
          parentId={comment.id}
          type={type}
          isUpdate={replyUpdate}
          onUpdate={setReplyUpdate}
          onAdded={onAdded}
        />
      )}
    </>
  );
}
